// User Documents API Types
// Enables users to upload their own documents (PDFs, DOCX, etc.) to enhance Evidence search

// ===== ENUMS =====

export type UserDocumentScope = 'user' | 'team';

export type UserDocumentStatus =
  | 'pending'
  | 'extracting'
  | 'chunking'
  | 'embedding'
  | 'completed'
  | 'failed';

// ===== JOB (Upload Response) =====

// Backend returns JobResponse from upload, NOT a document
// Document is created when job completes successfully
export type JobStatus =
  | 'pending'
  | 'extracting'
  | 'chunking'
  | 'embedding'
  | 'completed'
  | 'failed';

export interface JobResponse {
  job_id: string;
  filename: string;
  scope: UserDocumentScope;
  team_id: string | null;
  status: JobStatus;
  error_message: string | null;
  uploaded_by: string;
  created_at: string;
  /** Whether this job can be retried (failed + has s3_key) */
  retryable: boolean;
  /** Client-generated UUID for idempotent uploads and optimistic reconciliation */
  client_upload_id?: string | null;
  /** Target collection for auto-add when processing completes */
  collection_id?: string | null;
}

// Alias for backwards compatibility
export type UploadDocumentResponse = JobResponse;

// ===== STATUS =====

export interface ProcessingProgress {
  stage: UserDocumentStatus;
  chunks_processed: number;
  chunks_total: number;
  message: string | null;
}

export interface DocumentStatusResponse {
  document_id: string;
  filename: string;
  scope: UserDocumentScope;
  status: UserDocumentStatus;
  progress: ProcessingProgress;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

// ===== LIST =====

// Document metadata extracted from file (nested structure from backend)
export interface DocumentMetadata {
  title?: string | null;
  author?: string | null;
  page_count?: number | null;
  word_count?: number | null;
  keywords?: string[] | null;
  description?: string | null;
  document_created_at?: string | null;
  document_modified_at?: string | null;
  source_format?: string | null;
  /**
   * EVI-1625: coarse OCR/text-extraction quality recorded at ingestion. Absent
   * for documents uploaded before the feature shipped (backfilled later).
   */
  ocr_quality?: { label: 'ok' | 'warning' | 'error' } | null;
}

// Provenance for a UserDocument row (T12 BE):
// - 'user'       : direct upload (legacy default; field omitted on older rows)
// - 'sharepoint' : ingested via the SharePoint integration; drives the
//                  Library page's filter pill (T13), per-document logo +
//                  "View only" badge (T14), and the "From SharePoint"
//                  citation sub-label (T15)
export type UserDocumentSource = 'user' | 'sharepoint';

export type UserDocumentKind = 'input' | 'artifact' | 'workflow_context';

// Note: Completed documents returned by backend do NOT have a status field
// They are only in the documents[] array when they're completed
export interface UserDocumentItem {
  document_id: string;
  filename: string;
  scope: UserDocumentScope;
  team_id: string | null;
  file_type?: string;
  file_size_bytes?: number;
  chunk_count: number;
  uploaded_by: string;
  created_at: string;
  // Status is optional - completed documents from backend don't have it
  // Legacy DocumentRow component uses this for displaying status badges
  status?: UserDocumentStatus;

  /** Origin of this document; defaults to 'user' when the backend omits it. */
  source?: UserDocumentSource;

  /** Agent-generated Work outputs vs source/input uploads. */
  document_kind?: UserDocumentKind;

  // Document metadata (extracted from file, may be null for unsupported formats)
  document_metadata?: DocumentMetadata | null;
}

export interface DocumentListResponse {
  documents: UserDocumentItem[];
  total: number;
}

// ===== COMBINED (Jobs + Documents) =====

export interface JobListItem {
  job_id: string;
  filename: string;
  scope: UserDocumentScope;
  team_id: string | null;
  status: JobStatus;
  error_message: string | null;
  uploaded_by: string;
  created_at: string;
  /** Whether this job can be retried (failed + has s3_key) */
  retryable: boolean;
  /** Client-generated UUID for idempotent uploads and optimistic reconciliation */
  client_upload_id?: string | null;
  /** Target collection for auto-add when processing completes */
  collection_id?: string | null;
}

export interface DocumentsAndJobsResponse {
  jobs: JobListItem[];
  documents: UserDocumentItem[];
}

/**
 * Combined list item that can represent either a job or a completed document.
 *
 * DECLARED HERE rather than beside `useDocumentsAndJobs`, which is where it
 * was written: `canModifyDocument` in `../types.ts` is a pure predicate over
 * it and has to stay inert, so the type cannot live in a hook module that
 * reads the cache. The hook keeps every other projection and re-exports this
 * name, so its importers are untouched.
 */
export interface LibraryItem {
  id: string;
  type: 'job' | 'document';
  filename: string;
  scope: 'user' | 'team';
  team_id: string | null;
  status: 'pending' | 'extracting' | 'chunking' | 'embedding' | 'completed' | 'failed';
  error_message: string | null;
  chunk_count: number;
  created_at: string;
  uploaded_by: string;
  /** Whether this failed job can be retried (has s3_key) */
  retryable: boolean;
  /**
   * Origin of this document. Only set for `type === 'document'`; jobs
   * don't yet know their final source (the file is mid-ingest). Drives
   * the per-document SharePoint logo + "View only" badge (T14) and the
   * Library "Sharepoint" filter pill (T13).
   */
  source?: UserDocumentSource;
  document_kind?: 'input' | 'artifact' | 'workflow_context';
  /** Target collection for auto-add when processing completes (jobs only) */
  collection_id?: string | null;
  // Document metadata (only present for completed documents)
  document_metadata?: DocumentMetadata | null;
}

// ===== FILE VALIDATION =====

// Single source of truth for upload allowlist. Each MIME entry lists every
// extension it accepts. Adding support for a new format = one entry here;
// the picker `accept` string and the extension→MIME fallback both derive.
export const SUPPORTED_FILE_TYPES = {
  'application/pdf': { maxSize: 100 * 1024 * 1024, extensions: ['.pdf'] },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': {
    maxSize: 100 * 1024 * 1024,
    extensions: ['.docx'],
  },
  'text/plain': { maxSize: 100 * 1024 * 1024, extensions: ['.txt'] },
  'text/markdown': { maxSize: 100 * 1024 * 1024, extensions: ['.md'] },
  'text/x-markdown': { maxSize: 100 * 1024 * 1024, extensions: ['.md'] },
  'application/json': { maxSize: 100 * 1024 * 1024, extensions: ['.json'] },
  'text/xml': { maxSize: 100 * 1024 * 1024, extensions: ['.xml'] },
  'application/xml': { maxSize: 100 * 1024 * 1024, extensions: ['.xml'] },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': {
    maxSize: 100 * 1024 * 1024,
    extensions: ['.xlsx'],
  },
  'application/zip': { maxSize: 100 * 1024 * 1024, extensions: ['.zip'] },
  'image/jpeg': { maxSize: 100 * 1024 * 1024, extensions: ['.jpg', '.jpeg'] },
  'image/png': { maxSize: 100 * 1024 * 1024, extensions: ['.png'] },
} as const;

export type SupportedMimeType = keyof typeof SUPPORTED_FILE_TYPES;

export const SUPPORTED_FILE_EXTENSION_TO_MIME: Readonly<
  Record<string, SupportedMimeType>
> = Object.freeze(
  Object.fromEntries(
    Object.entries(SUPPORTED_FILE_TYPES).flatMap(([mime, info]) =>
      info.extensions.map((ext) => [ext.replace(/^\./, ''), mime as SupportedMimeType])
    )
  )
);

export const USER_DOCUMENTS_ACCEPT_FORMATS: string = [
  ...new Set(
    Object.entries(SUPPORTED_FILE_TYPES).flatMap(([mime, info]) => [
      ...info.extensions,
      mime,
    ])
  ),
].join(',');

export const MAX_FILE_SIZE_MB = 100;

// ===== DOWNLOAD URL =====

/**
 * Response from the download URL endpoint.
 * Returns a pre-signed S3 URL for downloading/previewing the document.
 */
export interface DownloadUrlResponse {
  /** Pre-signed URL for downloading/viewing the document */
  url: string;
  /** ISO timestamp when the URL expires */
  expires_at: string;
  /** Original filename of the document */
  filename: string;
}
